/* eslint react/no-danger: 0 */
import React, { useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link, Switch, Route, Redirect } from 'react-router-dom';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Input,
  InputLabel,
  Typography,
  FormHelperText
} from '@mui/material';

import signInImage from 'url:../../assets/signin.jpg';
import agent from '../../lib/agent';
import axios from '../../lib/axios';
import useStores from '../../stores/useStores';
import LoadingSpinner from '../general/LoadingSpinner';
import './SignIn.sass';

function SignIn({ history, location, match }) {
  const { userStore } = useStores();
  const [isTokenValid, setIsTokenValid] = useState(null);
  const [error, setError] = useState({});
  const { resetPasswordToken, userActivationToken } = match.params;
  const emailInput = useRef(null);
  const passwordInput = useRef(null);
  const passwordRepeatInput = useRef(null);
  const honeyPotInput = useRef(null);
  const [passwordResetEmailSent, setPasswordResetEmailSent] = useState(false);
  const [userIsActivated, setUserIsActivated] = useState(null);

  useEffect(() => {
    if (!resetPasswordToken) return;
    (async () => {
      const response = await agent.User.validateResetToken(resetPasswordToken);
      setIsTokenValid(response.data.is_valid);
    })();
  }, [resetPasswordToken]);

  useEffect(() => {
    if (!userActivationToken) return;
    (async () => {
      const response = await agent.User.validateUserActivationToken(
        userActivationToken
      );

      await userStore.fetchCredentials();
      const { redirect } = response.data;
      if (redirect) {
        history.push(redirect);
      }
      setUserIsActivated(response.data.ok === 1);
    })();
  }, [userActivationToken]);

  const handleSubmit = async e => {
    e.preventDefault();
    // if (honeyPotInput?.current?.value) return;

    if (resetPasswordToken) {
      const newPassword = passwordInput.current.value;
      const newPasswordRepeat = passwordRepeatInput.current.value;
      if (newPassword !== newPasswordRepeat) {
        setError({
          'password-repeat': 'The password confirmation does not match'
        });
        return;
      }
      setError({});

      const { data } = await axios.post(`/reset/${resetPasswordToken}`, {
        password: newPassword,
        resetPasswordToken
      });

      if (data.ok) {
        return history.push('/');
      }
    }

    const email = emailInput.current.value;
    const password = passwordInput.current.value;
    const success = await userStore.login(email, password);

    if (!success) {
      setError({
        'password-error': 'Wrong username/password combination'
      });
      passwordInput.current.value = '';
    } else {
      history.push(location.state?.referrer || '/');
    }
  };

  const handlePasswordResetSubmit = event => {
    event.preventDefault();
    const email = emailInput.current.value;
    agent.User.sendPasswordResetEmail(email);
    setPasswordResetEmailSent(true);
  };

  const tosText = {
    __html: `I agree with the <a target="_blank" href="https://gridmarket.com/terms-of-use/">Terms Of Use</a> and <a target="_blank" href="https://gridmarket.com/privacy-policy/">Privacy Policy</a>`
  };
  return (
    <div className="sign-in">
      <main>
        <Switch>
          <Route
            path="/forgot-password"
            render={() => (
              <>
                <form onSubmit={handlePasswordResetSubmit}>
                  {passwordResetEmailSent && (
                    <div>
                      <Typography align="center" variant="h6">
                        Password reset e-mail has been sent
                      </Typography>
                      <Typography gutterBottom align="center" variant="body2">
                        Password reset e-mail has been sent to you, check your
                        e-mail
                      </Typography>
                      <Typography align="center" variant="body2">
                        <Link to="/">Back to login</Link>
                      </Typography>
                    </div>
                  )}

                  {!passwordResetEmailSent && (
                    <div>
                      <Typography align="center" variant="h6">
                        Reset your password
                      </Typography>
                      <Typography align="center" variant="body2">
                        Type in your e-mail and we&apos;ll send you the link to
                        reset your password
                      </Typography>
                      <FormControl margin="normal" required fullWidth>
                        <InputLabel htmlFor="email">Email Address</InputLabel>
                        <Input
                          inputRef={emailInput}
                          id="email"
                          name="email"
                          autoComplete="email"
                          type="email"
                          autoFocus
                        />
                      </FormControl>

                      <div className="sign-in-buttons">
                        <Button
                          type="submit"
                          variant="outlined"
                          color="primary"
                          size="large"
                        >
                          Sendd
                        </Button>
                      </div>
                    </div>
                  )}
                </form>
              </>
            )}
          />
          <Route
            path="/login/activate/:userActivationToken"
            exact
            render={() => (
              <>
                {userIsActivated === true && (
                  <div>
                    <Redirect
                      to={{
                        pathname: '/',
                        search: ''
                      }}
                    />
                  </div>
                )}
                {userIsActivated === false && (
                  <div>
                    <Typography align="center" variant="h6">
                      Your account activation link is invalid
                    </Typography>
                    <Typography gutterBottom align="center" variant="body2">
                      Please check your e-mail for the correct link
                    </Typography>
                    <Typography align="center" variant="body2">
                      <Link to="/">Back to login</Link>
                    </Typography>
                  </div>
                )}
              </>
            )}
          />

          <Route
            path="/login"
            exact
            render={() => (
              <form onSubmit={handleSubmit}>
                <Typography align="center" variant="h6">
                  Sign in
                </Typography>
                <FormControl margin="normal" required fullWidth>
                  <InputLabel htmlFor="email">Email Address</InputLabel>
                  <Input
                    inputRef={emailInput}
                    id="email"
                    name="email"
                    autoComplete="email"
                    autoFocus
                  />
                </FormControl>
                <FormControl margin="normal" required fullWidth>
                  <InputLabel htmlFor="password">Password</InputLabel>
                  <Input
                    inputRef={passwordInput}
                    name="password"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                  />
                  <FormHelperText error>
                    {error['password-error']}
                  </FormHelperText>
                </FormControl>
                <FormControl
                  style={{
                    transform: 'translateX(-10000px)',
                    height: 1,
                    margin: 0,
                    overflow: 'hidden',
                    position: 'fixed'
                  }}
                  fullWidth
                >
                  <Input
                    style={{
                      transform: 'translateX(-10000px)',
                      height: 1,
                      display: 'none'
                    }}
                    inputRef={honeyPotInput}
                    name="password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                  />
                </FormControl>
                <Grid
                  justifyContent="flex-end"
                  alignItems="right"
                  container
                  spacing={3}
                >
                  <Grid item>
                    <Link to="/forgot-password">Forgot your password?</Link>
                  </Grid>
                </Grid>
                <Grid
                  justifyContent="flex-end"
                  alignItems="right"
                  container
                  spacing={3}
                >
                  <Grid
                    style={{
                      display: 'none'
                    }}
                    item
                  >
                    <Link color="primary" to="/auth/arcgis">
                      Sign in with ArcGIS
                    </Link>
                  </Grid>
                </Grid>

                <div className="sign-in-buttons">
                  <Button
                    type="submit"
                    variant="outlined"
                    color="primary"
                    size="large"
                  >
                    {resetPasswordToken && <span>Submit</span>}
                    {!resetPasswordToken && <span>Sign in</span>}
                  </Button>
                </div>
              </form>
            )}
          />

          <Route exact path="/login/:token">
            <>
              {isTokenValid === null && <LoadingSpinner />}
              {isTokenValid === false && (
                <div>
                  <Typography align="center" variant="h6">
                    Your link has expired
                  </Typography>
                  <Typography align="center" variant="body1">
                    Your account activation link has expired. If you have
                    already activated your account then{' '}
                    <Link to="/">sign in</Link>.
                  </Typography>
                </div>
              )}
              {isTokenValid === true && (
                <>
                  <form onSubmit={handleSubmit}>
                    <Typography align="center" variant="h6">
                      Create your new password
                    </Typography>
                    <FormControl margin="normal" required fullWidth>
                      <InputLabel htmlFor="password">Password</InputLabel>
                      <Input
                        inputProps={{
                          minlength: 6
                        }}
                        autoComplete="current-password"
                        id="password"
                        inputRef={passwordInput}
                        name="password"
                        type="password"
                      />
                    </FormControl>
                    <FormControl margin="normal" required fullWidth>
                      <InputLabel htmlFor="password">
                        Repeat Password
                      </InputLabel>
                      <Input
                        autoComplete="current-password"
                        id="password-repeat"
                        error={error['password-repeat']}
                        inputRef={passwordRepeatInput}
                        name="password-repeat"
                        type="password"
                      />
                      <FormHelperText id="my-helper-text">
                        {error['password-repeat']}
                      </FormHelperText>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox required value="tos" color="primary" />
                      }
                      label={<span dangerouslySetInnerHTML={tosText} />}
                    />
                    <div className="sign-in-buttons">
                      <Button
                        type="submit"
                        variant="outlined"
                        color="primary"
                        size="large"
                      >
                        {resetPasswordToken && <span>Submit</span>}
                        {!resetPasswordToken && <span>Sign in</span>}
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </>
          </Route>
        </Switch>
      </main>
      <div
        className="sign-in-background"
        style={{ backgroundImage: `url(${signInImage})` }}
      />
    </div>
  );
}

export default observer(SignIn);
